/**
 * DraftsTab — the "Drafts" tab within RequirementsPage.
 *
 * Three-state (loading skeleton / empty / error+Retry) per ui-ux-pro-max rules.
 * Columns: Request snippet, Status chip, Last updated, Projects chips, Actions.
 * Actions: Resume (navigate) + Discard with a confirm step.
 *
 * ui-ux-pro-max: 8dp spacing, palette colors, 44px touch targets,
 * role="alert" on errors, role="status" on loading.
 */

import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useCallback, useState, type ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';

import { useIntakeDrafts } from '@/hooks/useIntakeDrafts';
import type { IntakeDraftSummary } from '@/types/intake';

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

const SNIPPET_MAX = 80;
function snippet(text: string): string {
  if (text.length <= SNIPPET_MAX) return text;
  return `${text.slice(0, SNIPPET_MAX).trimEnd()}…`;
}

function statusLabel(draft: IntakeDraftSummary): string {
  if (draft.failed_step_id) return 'failed step';
  if (draft.status === 'preview') return 'preview';
  return 'in progress';
}

function statusColor(
  draft: IntakeDraftSummary,
): 'default' | 'primary' | 'warning' | 'error' {
  if (draft.failed_step_id) return 'error';
  if (draft.status === 'preview') return 'primary';
  return 'default';
}

interface DraftRowProps {
  draft: IntakeDraftSummary;
  onResume: (id: string) => void;
  /** Returns an error message string on failure, null on success. */
  onDiscard: (id: string) => Promise<string | null>;
}

function DraftRow({ draft, onResume, onDiscard }: DraftRowProps): ReactElement {
  const [confirming, setConfirming] = useState(false);
  const [discarding, setDiscarding] = useState(false);
  const [discardError, setDiscardError] = useState<string | null>(null);

  const handleDiscardClick = useCallback(() => {
    setConfirming(true);
    setDiscardError(null);
  }, []);

  const handleDiscardConfirm = useCallback(async () => {
    setDiscarding(true);
    setDiscardError(null);
    const err = await onDiscard(draft.id);
    if (err) {
      setDiscardError(err);
      setDiscarding(false);
      setConfirming(false);
    }
    // On success the row disappears (optimistic remove).
  }, [draft.id, onDiscard]);

  const handleDiscardCancel = useCallback(() => {
    setConfirming(false);
    setDiscardError(null);
  }, []);

  return (
    <TableRow hover>
      <TableCell sx={{ maxWidth: 320 }}>
        <Typography
          variant="body2"
          sx={{
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: 2,
            overflow: 'hidden',
          }}
          title={draft.initial_request}
        >
          {snippet(draft.initial_request)}
        </Typography>
        {draft.title && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
            {snippet(draft.title)}
          </Typography>
        )}
        {discardError && (
          <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
            {discardError}
          </Typography>
        )}
      </TableCell>
      <TableCell>
        <Chip
          label={statusLabel(draft)}
          size="small"
          color={statusColor(draft)}
          variant="outlined"
        />
      </TableCell>
      <TableCell sx={{ whiteSpace: 'nowrap' }}>
        <Typography variant="caption" color="text.secondary">
          {relativeTime(draft.updated_at)}
        </Typography>
      </TableCell>
      <TableCell>
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
          {draft.projects.map((p) => (
            <Chip key={p} label={p} size="small" variant="outlined" color="primary" />
          ))}
        </Stack>
      </TableCell>
      <TableCell align="right">
        <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center">
          {confirming ? (
            <>
              <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
                Discard?
              </Typography>
              <Button
                size="small"
                color="error"
                variant="contained"
                onClick={handleDiscardConfirm}
                disabled={discarding}
                sx={{ minHeight: 44, textTransform: 'none' }}
              >
                {discarding ? <CircularProgress size={16} color="inherit" /> : 'Yes, discard'}
              </Button>
              <Button
                size="small"
                onClick={handleDiscardCancel}
                disabled={discarding}
                sx={{ minHeight: 44, textTransform: 'none' }}
              >
                Cancel
              </Button>
            </>
          ) : (
            <>
              <Button
                size="small"
                variant="outlined"
                onClick={() => onResume(draft.id)}
                sx={{ minHeight: 44, textTransform: 'none' }}
              >
                Resume
              </Button>
              <Button
                size="small"
                color="error"
                onClick={handleDiscardClick}
                sx={{ minHeight: 44, textTransform: 'none' }}
              >
                Discard
              </Button>
            </>
          )}
        </Stack>
      </TableCell>
    </TableRow>
  );
}

export function DraftsTab(): ReactElement {
  const navigate = useNavigate();
  const { drafts, error, refetch, discard } = useIntakeDrafts();

  const handleResume = useCallback(
    (id: string) => {
      navigate(`/requests/new/${id}`);
    },
    [navigate],
  );

  // Loading skeleton
  if (error === null && drafts === null) {
    return (
      <Stack spacing={1} role="status" aria-label="Loading drafts">
        {[...Array(3)].map((_, i) => (
          <Skeleton key={i} variant="rounded" height={52} />
        ))}
      </Stack>
    );
  }

  // Error state
  if (error !== null) {
    return (
      <Alert
        severity="error"
        role="alert"
        action={
          <Button size="small" color="inherit" onClick={() => void refetch()}>
            Retry
          </Button>
        }
      >
        {error}
      </Alert>
    );
  }

  // Empty state
  if (drafts !== null && drafts.length === 0) {
    return (
      <Box sx={{ py: 6, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          No drafts in progress.{' '}
          <Button
            variant="text"
            size="small"
            onClick={() => navigate('/requests/new')}
            sx={{ textTransform: 'none', p: 0, verticalAlign: 'baseline', minHeight: 'auto' }}
          >
            Start a new request
          </Button>{' '}
          to capture your next requirement.
        </Typography>
      </Box>
    );
  }

  return (
    <TableContainer>
      <Table size="small" aria-label="In-progress drafts">
        <TableHead>
          <TableRow>
            <TableCell>Request</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Last updated</TableCell>
            <TableCell>Projects</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {(drafts ?? []).map((draft) => (
            <DraftRow
              key={draft.id}
              draft={draft}
              onResume={handleResume}
              onDiscard={discard}
            />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
